<template>
    <div class="flex flex-col gap-8">
        <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold">Audio Recordings</h3>
            <vx-button
                size="sm"
                color="muted-light"
                data-test="templates-button"
                :loading="loadingTemplate"
                @click="templateRecordings()"
            >
                Download Templates
                <font-awesome-icon widthAuto :icon="faFileWaveform" />
            </vx-button>
        </div>

        <input
            type="file"
            id="import-file"
            class="opacity-0 absolute w-1 h-1"
            ref="importFile"
            accept=".zip"
            @change="uploadRecordings()"
        />

        <div v-if="loadingSounds" class="flex justify-center" data-test="loading-sound-spinner">
            <vx-icon-loading spin></vx-icon-loading>
        </div>
        <shift-to-text-sounds-table
            v-else
            @soundUpdated="updateSound"
            @import="importRecordings"
            @export="exportRecordings"
            :sounds="sounds"
            :loading-import="loadingImport"
            :loading-export="loadingExport"
        ></shift-to-text-sounds-table>
    </div>
</template>

<script>
import { isAuthErrorEcho } from '../../../../utils/helpers';
import ShiftToTextSoundsTable from './ShiftToTextSoundsTable.vue';
import { shiftToTextClient } from '~/services';
import { VxButton, VxIconLoading } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faFileWaveform } from '@fortawesome/pro-solid-svg-icons';

export default {
    components: {
        ShiftToTextSoundsTable,
        VxIconLoading,
        VxButton,
        FontAwesomeIcon,
    },
    data() {
        return {
            sounds: [],
            loadingSounds: false,
            loadingTemplate: false,
            loadingExport: false,
            loadingImport: false,
            echoErrors: [],
        };
    },
    computed: {
        faFileWaveform() {
            return faFileWaveform;
        },
    },
    mounted() {
        this.listenForEchoEvents();
        this.fetchSounds();
    },
    methods: {
        listenForEchoEvents() {
            const teamId = Spark.state.currentTeam.id;
            Echo.private(`shift_to_text_import.${teamId}`)
                .listen('ShiftToTextImportSuccessed', () => {
                    this.loadingImport = false;
                    this.$toasted.success('Your files were successfully imported');
                    this.fetchSounds();
                })
                .listen('ShiftToTextImportFailed', (event) => {
                    this.loadingImport = false;
                    this.$toasted.error(event.shift_to_text_import.message);
                })
                .error?.((e) => {
                    if (this.echoErrors.length <= 3 && !isAuthErrorEcho(e)) {
                        window?.Bugsnag?.notify(e instanceof Error ? e : new Error(JSON.stringify(e)));
                        this.echoErrors.push(e);
                    }
                });
        },
        importRecordings() {
            this.$refs.importFile.click();
        },
        importFile() {
            return this.$refs.importFile.files[0];
        },
        importFileSizeValid() {
            const file = this.importFile();
            if (!file) {
                return false;
            }
            if (!file?.size) {
                return false;
            }
            const fileSize = file.size / 1024 / 1024; // in MB
            if (fileSize > 20) {
                this.$toasted.error('Your file is too large, the size limit of your zip file should not exceed 20MB');
                return false;
            }
            return true;
        },
        uploadRecordings() {
            if (this.loadingImport) {
                return;
            }
            if (!this.importFileSizeValid()) {
                return;
            }
            const file = this.importFile();
            const teamId = Spark.state.currentTeam.id;
            this.loadingImport = true;
            this.$toasted.info("Don't close this window. Your files are being imported.");
            shiftToTextClient
                .import(teamId, file)
                .catch((error) => {
                    console.log(error);
                    this.$toasted.global.platform_error();
                    this.loadingImport = false;
                })
                .finally(() => {
                    this.$refs.importFile.value = '';
                });
        },
        templateRecordings() {
            if (this.loadingTemplate) {
                return;
            }
            this.loadingTemplate = true;
            const teamId = Spark.state.currentTeam.id;
            shiftToTextClient
                .templates(teamId)
                .catch(() => {
                    this.$toasted.global.platform_error();
                })
                .finally(() => {
                    this.loadingTemplate = false;
                });
        },
        exportRecordings() {
            if (this.loadingExport) {
                return;
            }
            this.loadingExport = true;
            const teamId = Spark.state.currentTeam.id;
            shiftToTextClient
                .export(teamId)
                .catch(() => {
                    this.$toasted.global.platform_error();
                })
                .finally(() => {
                    this.loadingExport = false;
                });
        },
        updateSound(sound) {
            const soundIndex = this.sounds.findIndex((previousSound) => previousSound.type === sound.type);
            if (soundIndex !== -1) {
                this.sounds[soundIndex] = sound;
            }
        },
        fetchSounds() {
            const teamId = Spark.state.currentTeam.id;
            this.loadingSounds = true;
            shiftToTextClient
                .sounds(teamId)
                .then((response) => {
                    const sounds = response?.data?.data;
                    this.sounds = sounds || [];
                })
                .catch(() => {
                    this.$toasted.global.platform_error();
                })
                .finally(() => {
                    this.loadingSounds = false;
                });
        },
    },
};
</script>
